import { orderProjectMilestones } from '~/lib/projectMilestones';
import type { Approval, Project, ProjectStage } from '~/types/models';

function normalized(value: unknown): string {
    return String(value || '').trim().toLowerCase();
}

const COMPLETED_PROJECT_STATUSES = new Set(['done', 'complete', 'completed']);
const ARCHIVED_PROJECT_STATUSES = new Set(['archived']);
const CANCELLED_PROJECT_STATUSES = new Set(['cancelled', 'canceled']);

export function normalizeProjectLifecycleStatus(status: unknown): string {
    return normalized(status);
}

export function isProjectCompleted(status: unknown): boolean {
    return COMPLETED_PROJECT_STATUSES.has(normalized(status));
}

export function isProjectArchived(status: unknown): boolean {
    return ARCHIVED_PROJECT_STATUSES.has(normalized(status));
}

export function isProjectInactive(status: unknown): boolean {
    const statusValue = normalized(status);
    return COMPLETED_PROJECT_STATUSES.has(statusValue)
        || ARCHIVED_PROJECT_STATUSES.has(statusValue)
        || CANCELLED_PROJECT_STATUSES.has(statusValue);
}

export function isProjectOperational(status: unknown): boolean {
    return !isProjectInactive(status);
}

function matchesStage(value: unknown, stage: ProjectStage): boolean {
    const candidate = normalized(value);
    return Boolean(candidate) && (candidate === normalized(stage.id) || candidate === normalized(stage.name));
}

export type ProjectCompletionUpdate = Pick<Project, 'status' | 'progress' | 'stages'> & {
    completedAt: unknown;
    updatedAt: unknown;
};

export function resolveFinalSignoffCompletion(
    project: Pick<Project, 'id' | 'status' | 'stage' | 'stages'>,
    approval: Pick<Approval, 'projectId' | 'stageName' | 'status'>,
    completedAt: unknown = new Date(),
): ProjectCompletionUpdate | null {
    if (normalized(project.status) === 'completed' || approval.status !== 'approved') return null;
    if (normalized(approval.projectId) !== normalized(project.id)) return null;

    const stages = orderProjectMilestones(project.stages || []);
    const finalStage = stages.at(-1);
    if (!finalStage?.requiresClientApproval) return null;
    if (!matchesStage(project.stage, finalStage) || !matchesStage(approval.stageName, finalStage)) return null;

    return {
        status: 'completed',
        progress: 100,
        completedAt,
        updatedAt: completedAt,
        stages: stages.map(stage => (
            stage.id === finalStage.id
                ? { ...stage, completed: true, completedAt: stage.completedAt || completedAt }
                : stage
        )),
    };
}
